import { Input } from "@/components/ui/input";
import {
	InputSideLabel,
	InputSideLabelPopover,
} from "@/components/ui/InputSideLabel";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { PlusIcon, TrashIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { t } from "i18next";
import type { ParameterInputProps } from "./types";
import { getInputClass } from "./utils";

export const IntervalsInput = ({
	store,
	insurance,
	parameter,
}: ParameterInputProps<"intervals">) => {
	return (
		<Popover>
			<PopoverTrigger asChild>
				<Button variant="outline">
					{t("admin.insurances.parameters.type-to-name.intervals")}
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-[430px]">
				<div>
					<div className="flex flex-col divide-y">
						{parameter.value.map((interval) => {
							return (
								<div
									key={interval.id}
									className="grid grid-cols-7 py-4 items-end gap-4"
								>
									<div className="col-span-2">
										<Label>
											{t("admin.insurances.parameters.type-to-name.min-age")}
										</Label>
										<div className="relative flex">
											<Input
												type="number"
												value={interval.minAge}
												onChange={(e) => {
													interval.minAge = e.target.value;
													store.editParameter(insurance, parameter);
												}}
												className={cn(getInputClass(parameter))}
											/>
											<Popover>
												<PopoverTrigger asChild>
													<InputSideLabel className={cn("cursor-pointer")}>
														{t(
															"admin.insurances.parameters.type-to-name.years"
														)}
													</InputSideLabel>
												</PopoverTrigger>
												<PopoverContent>
													<InputSideLabelPopover
														title={t(
															"admin.insurances.parameters.type-to-name.min-age"
														)}
														content={t(
															"admin.insurances.parameters.content.min-age-interval"
														)}
														error={parameter.error}
														isAltered={false}
														resetParam={() =>
															store.resetParameter(insurance, parameter)
														}
													/>
												</PopoverContent>
											</Popover>
										</div>
									</div>
									<div className="col-span-2">
										<Label>
											{t("admin.insurances.parameters.type-to-name.max-age")}
										</Label>
										<div className="relative flex">
											<Input
												type="number"
												value={interval.maxAge}
												onChange={(e) => {
													interval.maxAge = e.target.value;
													store.editParameter(insurance, parameter);
												}}
												className={cn(getInputClass(parameter))}
											/>
											<Popover>
												<PopoverTrigger asChild>
													<InputSideLabel className={cn("cursor-pointer")}>
														{t(
															"admin.insurances.parameters.type-to-name.years"
														)}
													</InputSideLabel>
												</PopoverTrigger>
												<PopoverContent>
													<InputSideLabelPopover
														title={t(
															"admin.insurances.parameters.type-to-name.max-age"
														)}
														content={t(
															"admin.insurances.parameters.content.max-age-interval"
														)}
														error={parameter.error}
														isAltered={false}
														resetParam={() =>
															store.resetParameter(insurance, parameter)
														}
													/>
												</PopoverContent>
											</Popover>
										</div>
									</div>
									<div className="col-span-2">
										<Label>
											{t(
												"admin.insurances.parameters.type-to-name.annual-salary"
											)}
										</Label>
										<div className="relative flex">
											<Input
												type="number"
												value={interval.value}
												onChange={(e) => {
													interval.value = e.target.value;
													store.editParameter(insurance, parameter);
												}}
												className={cn(getInputClass(parameter))}
											/>
											<Popover>
												<PopoverTrigger asChild>
													<InputSideLabel className={cn("cursor-pointer")}>
														x
													</InputSideLabel>
												</PopoverTrigger>
												<PopoverContent>
													<InputSideLabelPopover
														title={t(
															"admin.insurances.parameters.type-to-name.annual-salary"
														)}
														content={t(
															"admin.insurances.parameters.content.annual-salary"
														)}
														error={parameter.error}
														isAltered={false}
														resetParam={() =>
															store.resetParameter(insurance, parameter)
														}
													/>
												</PopoverContent>
											</Popover>
										</div>
									</div>
									<div>
										<Button
											size="icon"
											variant="destructive"
											onClick={() => {
												parameter.value = parameter.value.filter(
													(int) => int.id !== interval.id
												);
												store.editParameter(insurance, parameter);
											}}
										>
											<TrashIcon />
										</Button>
									</div>
								</div>
							);
						})}
					</div>
					<div className="col-span-3 flex items-center justify-center">
						<Button
							onClick={() => {
								parameter.value.push({
									id: crypto.randomUUID(),
									minAge: "",
									maxAge: "",
									value: "",
								});
								store.editParameter(insurance, parameter);
							}}
							className="w-fit"
							variant="secondary"
						>
							<PlusIcon />
							{t("admin.insurances.parameters.type-to-name.add-interval")}
						</Button>
					</div>
				</div>
			</PopoverContent>
		</Popover>
	);
};
